import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmt, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const JOB_TYPES = ["Music Video", "Film / TV", "Event", "Photoshoot", "Brand", "Other"];
const MODEL_PACKAGES = { 3: 1000, 5: 1500, 10: 3000, 15: 4500, 20: 6000 };
const CAR_CATEGORIES = ["Coupe", "Convertible", "SUV"];

export const BookingDialog = ({ type, listing, triggerLabel = "Request to Book", testId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", ig: "", tiktok: "", x: "", website: "", youtube: "", spotify: "",
    num_models: 1, package: "", duration: "4", category: "", date: "", hours: 1,
    job_type: "", location: "", attire: "", referral: "", notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.post("/bookings/estimate", {
        type,
        hours: Number(form.hours) || 1,
        num_models: Number(form.num_models) || 1,
        package: form.package ? Number(form.package) : null,
        duration: type === "house" ? Number(form.duration) : null,
        listing_id: listing?.id,
      }).then((r) => setEstimate(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [open, form.hours, form.num_models, form.package, form.duration, type, listing]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        type,
        listing_id: listing?.id,
        client: {
          name: form.name, email: form.email, phone: form.phone, ig: form.ig,
          tiktok: form.tiktok, x: form.x, website: form.website, youtube: form.youtube, spotify: form.spotify,
        },
        details: {
          num_models: Number(form.num_models) || 1,
          package: form.package ? Number(form.package) : null,
          duration: type === "house" ? Number(form.duration) : null,
          category: form.category || listing?.category || "",
          date: form.date, hours: Number(form.hours) || 1,
          job_type: form.job_type, location: type === "house" ? (listing?.location || "Los Angeles") : form.location,
          attire: form.attire, referral: form.referral, notes: form.notes,
        },
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Request received — our team will confirm pricing and follow up.");
        setOpen(false);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId || `book-${type}-btn`}
          className="bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-6 py-3 transition-colors">
          {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="booking-dialog"
        className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-white">
            Book {type === "model" ? "Models" : type === "car" ? "a Car" : "a House"}
            {listing ? ` — ${listing.title}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">Request-to-book form with client details and deposit estimate</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="text-xs text-zinc-400">Artist / Brand Name *</Label>
            <Input data-testid="booking-name" required value={form.name} onChange={set("name")} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email *</Label>
            <Input data-testid="booking-email" required type="email" value={form.email} onChange={set("email")} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Phone *</Label>
            <Input data-testid="booking-phone" required value={form.phone} onChange={set("phone")} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Instagram *</Label>
            <Input data-testid="booking-ig" required value={form.ig} onChange={set("ig")} className="bg-white/5 border-white/10 mt-1" placeholder="@handle" />
          </div>

          {type === "model" && (
            <>
              <div className="sm:col-span-2">
                <Label className="text-xs text-zinc-400">Package (fixed price, 4 hours)</Label>
                <Select value={form.package} onValueChange={set("package")}>
                  <SelectTrigger data-testid="booking-package" className="bg-white/5 border-white/10 mt-1">
                    <SelectValue placeholder="Custom — priced per model per hour" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="custom">Custom (choose models & hours)</SelectItem>
                    {Object.entries(MODEL_PACKAGES).map(([n, p]) => (
                      <SelectItem key={n} value={n}>{n} Models / 4 Hours — {fmt(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(!form.package || form.package === "custom") && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-400"># Models *</Label>
                    <Input data-testid="booking-num-models" required type="number" min="1" value={form.num_models}
                      onChange={set("num_models")} className="bg-white/5 border-white/10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Hours *</Label>
                    <Input data-testid="booking-hours" required type="number" min="1" value={form.hours}
                      onChange={set("hours")} className="bg-white/5 border-white/10 mt-1" />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs text-zinc-400">Attire</Label>
                <Input data-testid="booking-attire" value={form.attire} onChange={set("attire")} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </>
          )}

          {type === "car" && (
            <>
              <div>
                <Label className="text-xs text-zinc-400">Category *</Label>
                <Select value={form.category || listing?.category || ""} onValueChange={set("category")} required>
                  <SelectTrigger data-testid="booking-category" className="bg-white/5 border-white/10 mt-1">
                    <SelectValue placeholder="Coupe / Convertible / SUV" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {CAR_CATEGORIES.map((c) => <SelectItem key={c} value={c.toLowerCase()}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Hours *</Label>
                <Input data-testid="booking-hours" required type="number" min="1" value={form.hours}
                  onChange={set("hours")} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </>
          )}

          {type === "house" && (
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Duration (4 hr minimum) *</Label>
              <Select value={form.duration} onValueChange={set("duration")}>
                <SelectTrigger data-testid="booking-duration" className="bg-white/5 border-white/10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {["4", "6", "8"].map((h) => (
                    <SelectItem key={h} value={h}>
                      {h} Hours{listing?.price_tiers?.[h] ? ` — ${fmt(listing.price_tiers[h])}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-zinc-400">Date *</Label>
            <Input data-testid="booking-date" required type="date" value={form.date} onChange={set("date")} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Job Type *</Label>
            <Select value={form.job_type} onValueChange={set("job_type")} required>
              <SelectTrigger data-testid="booking-job-type" className="bg-white/5 border-white/10 mt-1">
                <SelectValue placeholder="Select job type" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {JOB_TYPES.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type !== "house" && (
            <div>
              <Label className="text-xs text-zinc-400">Location *</Label>
              <Input data-testid="booking-location" required value={form.location} onChange={set("location")} className="bg-white/5 border-white/10 mt-1" />
            </div>
          )}
          <div>
            <Label className="text-xs text-zinc-400">Where did you find us?</Label>
            <Input data-testid="booking-referral" value={form.referral} onChange={set("referral")} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-400">Important Info</Label>
            <Textarea data-testid="booking-notes" value={form.notes} onChange={set("notes")} className="bg-white/5 border-white/10 mt-1" rows={3} />
          </div>
          <details className="sm:col-span-2">
            <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300">Optional links (TikTok, X, Website, YouTube, Spotify)</summary>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              {[["tiktok", "TikTok"], ["x", "X"], ["website", "Website"], ["youtube", "YouTube"], ["spotify", "Spotify"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs text-zinc-400">{l}</Label>
                  <Input data-testid={`booking-${k}`} value={form[k]} onChange={set(k)} className="bg-white/5 border-white/10 mt-1" />
                </div>
              ))}
            </div>
          </details>
          <div className="sm:col-span-2 border border-white/10 p-4 space-y-2">
            {estimate && estimate.estimate > 0 ? (
              <div data-testid="booking-estimate" className="text-sm text-zinc-300 space-y-1">
                <div className="flex justify-between"><span>Base rate</span><span>{fmt(estimate.base)}</span></div>
                {estimate.travel_fee > 0 && (
                  <div className="flex justify-between"><span>Travel fee{type === "model" ? " (per model)" : ""}</span><span>{fmt(estimate.travel_fee)}</span></div>
                )}
                <div className="flex justify-between"><span>Tax</span><span className="text-green-400">$0 — No tax</span></div>
                <div className="flex justify-between border-t border-white/10 pt-2">
                  <span>Total</span><strong className="text-white">{fmt(estimate.estimate)}</strong>
                </div>
                <div className="flex justify-between text-brand">
                  <span>Deposit due today (50%)</span><strong>{fmt(estimate.deposit)}</strong>
                </div>
                <div className="flex justify-between text-zinc-500 text-xs">
                  <span>Balance after approval</span><span>{fmt(estimate.balance)}</span>
                </div>
              </div>
            ) : (
              <p data-testid="booking-quote-note" className="text-sm text-zinc-400">
                This request needs a custom quote — submit and our team will follow up.
              </p>
            )}
            <button data-testid="booking-submit-btn" type="submit" disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white text-xs font-semibold tracking-widest uppercase px-8 py-3.5 transition-colors">
              {loading ? "Processing…" : estimate && estimate.estimate > 0 ? "Pay Deposit & Book" : "Request Quote"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
